import type { ReactNode } from "react";

import * as React from "react";

import IconButton from "@replo/design-system/components/button/IconButton";
import EmptyState from "@replo/design-system/components/empty-state/EmptyState";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@replo/design-system/components/shadcn/core/pagination";
import {
  Table as ShadcnTable,
  TableBody as ShadcnTableBody,
  TableCaption as ShadcnTableCaption,
  TableCell as ShadcnTableCell,
  TableFooter as ShadcnTableFooter,
  TableHead as ShadcnTableHead,
  TableHeader as ShadcnTableHeader,
  TableRow as ShadcnTableRow,
} from "@replo/design-system/components/shadcn/core/table";
import { Skeleton } from "@replo/design-system/components/skeleton/Skeleton";
import Tooltip from "@replo/design-system/components/tooltip/Tooltip";
import { generatePageNumbersToShow } from "@replo/design-system/utils/pagination";
import twMerge from "@replo/design-system/utils/twMerge";
import { ArrowUpNarrowWide, Search } from "lucide-react";

type SortOrder = "ASC" | "DESC";

interface CommonColumnAttributes<DataType> {
  key: string;
  header: ReactNode;
  renderCell?: (row: DataType) => ReactNode;
  width?: string;
  align?: "left" | "right";
}

interface BaseSortableColumn<DataType>
  extends CommonColumnAttributes<DataType> {
  sortable: true;
  sortKey?: string;
  sortTooltip?: string;
}

interface BaseColumn<DataType> extends CommonColumnAttributes<DataType> {
  sortable?: false;
}

export type Column<DataType> =
  | BaseSortableColumn<DataType>
  | BaseColumn<DataType>;

interface SortConfig {
  sortKey: string;
  sortOrder: SortOrder;
  onSortChange: (sortKey: string, sortOrder: SortOrder) => void;
}

interface PaginationConfig {
  currentPage: number;
  totalRows: number;
  pageSize: number;
  onPageChange: (page: number) => void;
}

export type DataTableProps<DataType extends { key: string }> = {
  columns: Column<DataType>[];
  data: DataType[];
  onRowClick?: (row: DataType) => void;
  isLoading?: boolean;
  loadingRows?: number;
  caption?: ReactNode;
  footer?: ReactNode;
  emptyState?: ReactNode;
  sort?: SortConfig;
  pagination?: PaginationConfig;
  size?: "sm" | "base";
  variant?: "default" | "bordered";
  getRowClassName?: (row: DataType, index: number) => string | undefined;
  layoutClassName?: string;
  UNSAFE_className?: string;
  scrollable?: boolean;
  maxBodyHeight?: string;
  scrollContainerRef?: React.RefObject<HTMLDivElement>;
};

const TableRoot = React.forwardRef<
  HTMLTableElement,
  React.HTMLAttributes<HTMLTableElement>
>(({ className, ...props }, ref) => (
  <ShadcnTable
    ref={ref}
    className={twMerge("w-full caption-bottom text-sm table-fixed", className)}
    {...props}
  />
));
TableRoot.displayName = "TableRoot";

const TableHeader = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <ShadcnTableHeader
    ref={ref}
    className={twMerge("[&_tr]:border-b-0.5", className)}
    {...props}
  />
));
TableHeader.displayName = "TableHeader";

const TableBody = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <ShadcnTableBody ref={ref} className={className} {...props} />
));
TableBody.displayName = "TableBody";

const TableRow = React.forwardRef<
  HTMLTableRowElement,
  React.HTMLAttributes<HTMLTableRowElement> & { noHover?: boolean }
>(({ className, noHover = false, ...props }, ref) => (
  <ShadcnTableRow
    ref={ref}
    className={twMerge(
      "hover:bg-selectable-hover border-b-0.5 border-border",
      noHover && "hover:bg-transparent",
      props.onClick && "cursor-pointer",
      className,
    )}
    {...props}
  />
));
TableRow.displayName = "TableRow";

const TableHead = React.forwardRef<
  HTMLTableCellElement,
  React.ThHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <ShadcnTableHead
    ref={ref}
    className={twMerge(
      "text-muted bg-selectable-selected px-4 py-3 text-left align-middle text-xs font-medium uppercase tracking-wider",
      className,
    )}
    {...props}
  />
));
TableHead.displayName = "TableHead";

const TableCell = React.forwardRef<
  HTMLTableCellElement,
  React.TdHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <ShadcnTableCell
    ref={ref}
    className={twMerge(
      "px-4 py-3 align-middle text-sm whitespace-nowrap text-foreground overflow-hidden text-ellipsis",
      className,
    )}
    {...props}
  />
));
TableCell.displayName = "TableCell";

const TableFooter = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <ShadcnTableFooter ref={ref} className={className} {...props} />
));
TableFooter.displayName = "TableFooter";

const TableCaption = React.forwardRef<
  HTMLTableCaptionElement,
  React.HTMLAttributes<HTMLTableCaptionElement>
>(({ className, ...props }, ref) => (
  <ShadcnTableCaption ref={ref} className={className} {...props} />
));
TableCaption.displayName = "TableCaption";

const SortButton: React.FC<{
  sortKey: string;
  sortOrder: SortOrder;
  isActive: boolean;
  onChange: (sortKey: string, sortOrder: SortOrder) => void;
  tooltipText?: string;
  size?: "sm" | "base";
}> = ({
  sortKey,
  sortOrder,
  isActive,
  onChange,
  tooltipText,
  size = "base",
}) => {
  const handleClick = () => {
    const newOrder = isActive && sortOrder === "ASC" ? "DESC" : "ASC";
    onChange(sortKey, newOrder);
  };

  const sizeStyles = {
    sm: "h-5 w-5",
    base: "h-6 w-6",
  };

  const iconSizeStyles = {
    sm: "h-4 w-4",
    base: "h-[18px] w-[18px]",
  };

  const button = (
    <IconButton
      icon={
        <ArrowUpNarrowWide
          className={twMerge(
            iconSizeStyles[size],
            sortOrder === "DESC" && "scale-y-[-1] transform",
            isActive && "text-primary",
            !isActive && "text-placeholder",
          )}
        />
      }
      variant="tertiary"
      layoutClassName={sizeStyles[size]}
      onClick={handleClick}
    />
  );

  if (tooltipText) {
    return (
      <Tooltip
        triggerAsChild
        content={`${tooltipText} ${sortOrder === "ASC" ? "Ascending" : "Descending"}`}
        delay={0}
      >
        {button}
      </Tooltip>
    );
  }

  return button;
};

const TablePagination: React.FC<{
  currentPage: number;
  totalRows: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  layoutClassName?: string;
}> = ({ currentPage, totalRows, pageSize, onPageChange, layoutClassName }) => {
  const pageNumbersToShow = generatePageNumbersToShow(
    currentPage,
    totalRows,
    pageSize,
  );
  const totalPages = Math.ceil(totalRows / pageSize);
  const isFirstPage = currentPage === 1;
  const isLastPage = currentPage === totalPages;

  return (
    <Pagination className={layoutClassName}>
      <PaginationContent className="gap-4">
        <PaginationItem
          onClick={() => {
            if (!isFirstPage) {
              onPageChange(currentPage - 1);
            }
          }}
        >
          <PaginationPrevious
            isNavigationButton={true}
            isDisabled={isFirstPage}
            className="gap-1"
          />
        </PaginationItem>
        <div className="flex flex-row gap-1">
          {pageNumbersToShow.map((pageNumber, index) => {
            const isEllipsis =
              typeof pageNumber === "string" &&
              pageNumber.startsWith("ellipsis");
            return isEllipsis ? (
              <PaginationItem key={`${pageNumber}-${index}`}>
                <PaginationEllipsis className="pt-3" />
              </PaginationItem>
            ) : (
              <PaginationItem
                key={`page-number-${pageNumber}`}
                onClick={() => onPageChange(Number(pageNumber))}
              >
                <PaginationLink
                  isNavigationButton={false}
                  isActive={Number(pageNumber) === currentPage}
                >
                  {pageNumber}
                </PaginationLink>
              </PaginationItem>
            );
          })}
        </div>
        <PaginationItem
          onClick={() => {
            if (!isLastPage) {
              onPageChange(currentPage + 1);
            }
          }}
        >
          <PaginationNext
            isNavigationButton={true}
            isDisabled={isLastPage}
            className="gap-1"
          />
        </PaginationItem>
      </PaginationContent>
    </Pagination>
  );
};

const TableBodyRows = <DataType extends { key: string }>({
  columns,
  data,
  onRowClick,
  getRowClassName,
}: Pick<
  DataTableProps<DataType>,
  "columns" | "data" | "onRowClick" | "getRowClassName"
>) => {
  return (
    <>
      {data.map((row: DataType, index: number) => {
        const rowClassName = getRowClassName?.(row, index);
        return (
          <TableRow
            key={row.key}
            onClick={onRowClick ? () => onRowClick(row) : undefined}
            className={rowClassName}
          >
            {columns.map((column: Column<DataType>) => (
              <TableCell
                key={column.key}
                className={twMerge(
                  "text-base",
                  column.align === "right" && "text-right",
                )}
                style={{ width: column.width }}
              >
                {column.renderCell
                  ? column.renderCell(row)
                  : (row[column.key as keyof DataType] as React.ReactNode) ??
                    "N/A"}
              </TableCell>
            ))}
          </TableRow>
        );
      })}
    </>
  );
};

const TableLoader: React.FC<{
  rows: number;
  columns: number;
  size?: "sm" | "base";
}> = ({ rows, columns, size = "base" }) => {
  const skeletonHeight = size === "sm" ? "h-4" : "h-7";
  const cellPadding = size === "sm" ? "py-2" : "py-3";

  return (
    <>
      {Array.from({ length: rows }).map((_, index) => (
        <TableRow key={index}>
          {Array.from({ length: columns }).map((_, index) => (
            <TableCell key={index} className={cellPadding}>
              <Skeleton className={`${skeletonHeight} w-full`} />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  );
};

const TableEmptyState: React.FC<{
  columnsCount: number;
  emptyState: ReactNode;
}> = ({ columnsCount, emptyState }) => {
  return (
    <TableRow noHover>
      <TableCell colSpan={columnsCount} className="h-[500px] p-0">
        {emptyState ?? (
          <EmptyState
            title="No results"
            description="No results found"
            icon={<Search size={16} />}
          />
        )}
      </TableCell>
    </TableRow>
  );
};

const ScrollableTable = <DataType extends { key: string }>({
  columns,
  data,
  onRowClick,
  isLoading,
  loadingRows = 5,
  caption,
  footer,
  emptyState,
  sort,
  pagination,
  size,
  variant,
  getRowClassName,
  layoutClassName,
  UNSAFE_className,
  maxBodyHeight,
  scrollContainerRef,
}: DataTableProps<DataType> & { maxBodyHeight?: string }) => {
  const actualColumns = columns.length;

  let tableBodyContent: React.ReactNode;

  if (isLoading) {
    tableBodyContent = (
      <TableLoader rows={loadingRows} columns={actualColumns} size={size} />
    );
  } else if (data.length > 0) {
    tableBodyContent = (
      <TableBodyRows
        columns={columns}
        data={data}
        onRowClick={onRowClick}
        getRowClassName={getRowClassName}
      />
    );
  } else {
    tableBodyContent = (
      <TableEmptyState columnsCount={actualColumns} emptyState={emptyState} />
    );
  }

  const sizeStyles = {
    sm: "text-sm",
    base: "text-base",
  };

  const variantStyles = {
    default: "",
    bordered: "border border-border rounded-lg overflow-hidden",
  };

  return (
    <div
      className={twMerge(
        "flex flex-col min-h-0",
        layoutClassName,
        UNSAFE_className,
      )}
    >
      <div
        className={twMerge(
          "flex flex-col flex-1 min-h-0",
          variantStyles[variant!],
        )}
      >
        {caption && <TableCaption>{caption}</TableCaption>}

        {/* Fixed Header */}
        <div className="overflow-hidden">
          <table className={twMerge("w-full table-fixed", sizeStyles[size!])}>
            <TableHeader className="sticky top-0 bg-background">
              <TableRow>
                {columns.map((column: Column<DataType>, index: number) => (
                  <TableHead
                    key={column.key}
                    style={{ width: column.width }}
                    className={twMerge(
                      column.align === "right" && "text-right",
                      variant === "bordered" && index === 0 && "rounded-tl-lg",
                      variant === "bordered" &&
                        index === columns.length - 1 &&
                        "rounded-tr-lg",
                    )}
                  >
                    {column.sortable && sort ? (
                      <div className="flex items-center gap-2">
                        <span>{column.header}</span>
                        <SortButton
                          sortKey={column.sortKey ?? column.key}
                          sortOrder={sort.sortOrder}
                          isActive={
                            sort.sortKey === (column.sortKey ?? column.key)
                          }
                          onChange={sort.onSortChange}
                          tooltipText={column.sortTooltip}
                          size={size}
                        />
                      </div>
                    ) : (
                      column.header
                    )}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
          </table>
        </div>

        {/* Scrollable Body */}
        <div
          ref={scrollContainerRef}
          className={twMerge(
            "overflow-y-auto",
            !maxBodyHeight && "flex-1 min-h-0",
          )}
          style={maxBodyHeight ? { maxHeight: maxBodyHeight } : undefined}
        >
          <table className={twMerge("w-full table-fixed", sizeStyles[size!])}>
            <TableBody>{tableBodyContent}</TableBody>
          </table>
        </div>

        {footer && (
          <div className="overflow-hidden">
            <table className={twMerge("w-full table-fixed", sizeStyles[size!])}>
              <TableFooter>{footer}</TableFooter>
            </table>
          </div>
        )}
      </div>

      {pagination &&
        !isLoading &&
        data.length > 0 &&
        pagination.totalRows > 0 && (
          <TablePagination
            currentPage={pagination.currentPage}
            totalRows={pagination.totalRows}
            pageSize={pagination.pageSize}
            onPageChange={pagination.onPageChange}
            layoutClassName="mt-4"
          />
        )}
    </div>
  );
};

const Table = <DataType extends { key: string }>({
  columns,
  data,
  onRowClick,
  isLoading,
  loadingRows = 5,
  caption,
  footer,
  emptyState,
  sort,
  pagination,
  size = "base",
  variant = "default",
  getRowClassName,
  layoutClassName,
  UNSAFE_className,
  scrollable = false,
  maxBodyHeight,
  scrollContainerRef,
}: DataTableProps<DataType>) => {
  const actualColumns = columns.length;

  let tableBodyContent: React.ReactNode;

  if (isLoading) {
    tableBodyContent = (
      <TableLoader rows={loadingRows} columns={actualColumns} size={size} />
    );
  } else if (data.length > 0) {
    tableBodyContent = (
      <TableBodyRows
        columns={columns}
        data={data}
        onRowClick={onRowClick}
        getRowClassName={getRowClassName}
      />
    );
  } else {
    tableBodyContent = (
      <TableEmptyState columnsCount={actualColumns} emptyState={emptyState} />
    );
  }

  const sizeStyles = {
    sm: "text-sm",
    base: "text-base",
  };

  const variantStyles = {
    default: "",
    bordered: "border border-border rounded-lg overflow-hidden",
  };

  if (scrollable) {
    return (
      <ScrollableTable
        columns={columns}
        data={data}
        onRowClick={onRowClick}
        isLoading={isLoading}
        loadingRows={loadingRows}
        caption={caption}
        footer={footer}
        emptyState={emptyState}
        sort={sort}
        pagination={pagination}
        size={size}
        variant={variant}
        getRowClassName={getRowClassName}
        layoutClassName={layoutClassName}
        UNSAFE_className={UNSAFE_className}
        maxBodyHeight={maxBodyHeight}
        scrollContainerRef={scrollContainerRef}
      />
    );
  }

  return (
    <div
      className={twMerge(
        "flex flex-col overflow-y-auto",
        layoutClassName,
        UNSAFE_className,
      )}
    >
      <TableRoot
        className={twMerge(sizeStyles[size], variantStyles[variant], "flex-1")}
      >
        {caption && <TableCaption>{caption}</TableCaption>}
        <TableHeader className="sticky top-0">
          <TableRow>
            {columns.map((column: Column<DataType>, index: number) => (
              <TableHead
                key={column.key}
                style={{ width: column.width }}
                className={twMerge(
                  column.align === "right" && "text-right",
                  variant === "bordered" && index === 0 && "rounded-tl-lg",
                  variant === "bordered" &&
                    index === columns.length - 1 &&
                    "rounded-tr-lg",
                )}
              >
                {column.sortable && sort ? (
                  <div className="flex items-center gap-2">
                    <span>{column.header}</span>
                    <SortButton
                      sortKey={column.sortKey ?? column.key}
                      sortOrder={sort.sortOrder}
                      isActive={sort.sortKey === (column.sortKey ?? column.key)}
                      onChange={sort.onSortChange}
                      tooltipText={column.sortTooltip}
                      size={size}
                    />
                  </div>
                ) : (
                  column.header
                )}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>{tableBodyContent}</TableBody>
        {footer && <TableFooter>{footer}</TableFooter>}
      </TableRoot>
      {pagination &&
        !isLoading &&
        data.length > 0 &&
        pagination.totalRows > 0 && (
          <TablePagination
            currentPage={pagination.currentPage}
            totalRows={pagination.totalRows}
            pageSize={pagination.pageSize}
            onPageChange={pagination.onPageChange}
            layoutClassName="mt-4"
          />
        )}
    </div>
  );
};

Table.Root = TableRoot;
Table.Header = TableHeader;
Table.Body = TableBody;
Table.Row = TableRow;
Table.Head = TableHead;
Table.Cell = TableCell;
Table.Footer = TableFooter;
Table.Caption = TableCaption;
Table.Loader = TableLoader;
Table.Pagination = TablePagination;
Table.SortButton = SortButton;

export default Table;
export type { SortOrder, SortConfig, PaginationConfig };
